import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getGenres, getVideosWithCounts } from '../lib/queryOptimizer'
import { videoCache, commentCache } from '../lib/cache'

interface DataContextType {
  genres: any[]
  homeVideos: any[]
  refreshHomeVideos: () => Promise<void>
  invalidateCache: (key: string) => void
}

const DataContext = createContext<DataContextType>({
  genres: [],
  homeVideos: [],
  refreshHomeVideos: async () => {},
  invalidateCache: () => {}
})

export const useData = () => useContext(DataContext)

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [genres, setGenres] = useState<any[]>([])
  const [homeVideos, setHomeVideos] = useState<any[]>([])

  useEffect(() => {
    loadGenres()
    loadHomeVideos()
  }, [])

  const loadGenres = async () => {
    const data = await getGenres()
    setGenres(data)
  }

  const loadHomeVideos = async () => {
    const data = await getVideosWithCounts({ isPublic: true, sortBy: 'newest', limit: 50 })
    setHomeVideos(data)
  }

  const refreshHomeVideos = useCallback(async () => {
    await loadHomeVideos()
  }, [])

  const invalidateCache = useCallback((key: string) => {
    // Clear specific cache keys
    if (key === 'videos') videoCache.clear()
    if (key === 'comments') commentCache.clear()
    if (key === 'all') {
      videoCache.clear()
      commentCache.clear()
    }
  }, [])

  return (
    <DataContext.Provider value={{
      genres,
      homeVideos,
      refreshHomeVideos,
      invalidateCache
    }}>
      {children}
    </DataContext.Provider>
  )
}